import React, { useState, useEffect, useRef } from 'react';
import { BACKEND_URL, useOasisBackend } from '../../hooks/useOasisBackend';
import SettingsButton from '../components/SettingsButton';
import SearchBar from '../components/SearchBar';
import DomainResultCard from '../components/DomainResultCard';
import LoadingCard from '../components/LoadingCard';
import type { DomainResult } from '../../api/OasisBackendApi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { LoginItem } from '../../components/Login';
import { SignUpItem } from '../../components/SignupButton';
import { LogoutItem } from '../../components/LogoutButton';
import { useLoggedOutUserSearchCounter } from '../hooks/useLoggedOutUserSearchCounter';
import ReconnectingWebSocket from 'reconnecting-websocket';

interface WebSocketMessage {
  type: 'domain_search';
  data: DomainResult[];
}

const DomainSearchPage: React.FC = () => {
  const db = useOasisBackend();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [domainResults, setDomainResults] = useState<DomainResult[]>([]);
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementSearchCount, LimitReachedMessage, hasReachedLimit, showLimitReached, setShowLimitReached, isInitialized } = useLoggedOutUserSearchCounter(loggedIn);

  // Get initial query and domain from URL
  const initialQuery = searchParams.get('q') || '';
  const domain = searchParams.get('domain') || '';

  const ws = useRef<ReconnectingWebSocket | null>(null);

  // Establish websocket connection
  useEffect(() => {
    let prefix = 'wss://';
    if (BACKEND_URL.includes('127.0.0.1')) {
      prefix = 'ws://';
    }
    const wsUrl = `${prefix}${BACKEND_URL.replace(/^https?:\/\//, '')}/ws/rhea/`;
    
    ws.current = new ReconnectingWebSocket(wsUrl);
    
    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data) as WebSocketMessage;
      console.log("WebSocket message received:", data);

      if (data.type === 'domain_search') {
        const mappedResults: DomainResult[] = (data.data as DomainResult[]).map((result) => ({
          title: result.title || '',
          description: result.description || '',
          url: result.url || '#',
          image_url: result.image_url || '/assets/rhea/placeholder.png',
          type: result.type || '',
          domain: result.domain || ''
        }));
        setDomainResults(mappedResults);
        setIsLoading(false);
      }
    };

    ws.current.onopen = () => {
      console.log("WebSocket connection established");
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.current.onclose = () => {
      console.log("WebSocket connection closed");
    };

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  // Perform search if there's an initial query
  useEffect(() => {
    if (initialQuery && domain && isInitialized) {
      if (!loggedIn && hasReachedLimit) {
        setShowLimitReached(true);
        return;
      }
      handleSearch(initialQuery);
    }
  }, [initialQuery, domain, isInitialized]);

  const handleSearch = async (query: string) => {
    if (!loggedIn && hasReachedLimit) {
      setShowLimitReached(true);
      return;
    }

    if (query) {
      navigate(`/agentic?agent=rhea_domain&q=${encodeURIComponent(query)}&domain=${encodeURIComponent(domain)}`);
    } else {
      navigate('/agentic?agent=rhea_domain&domain=${encodeURIComponent(domain)}');
    }

    if (!loggedIn) {
      incrementSearchCount();
    }

    setIsLoading(true);
    setDomainResults([]);

    try {
      db.endpoints.rhea.rheaApiDomainSearchCreate({ q: query, domain: domain })
        .then(() => {
          // Results will come through WebSocket
        })
        .catch(() => {
          setDomainResults([]);
          setIsLoading(false);
        });
    } catch (error) {
      setDomainResults([]);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--rhea-bg-primary)', color: 'var(--rhea-text-primary)' }}>
      <LimitReachedMessage />
      {initialQuery || isLoading || domainResults.length > 0 ? (
        <>
          <div className="sticky top-0 z-10 bg-inherit border-b" style={{ borderColor: 'var(--rhea-card-border)' }}>
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.location.href = '/'}>
                  <img src="/assets/rhea/logo.png" alt="Rhea" className="h-8" />
                  <span className="text-xl font-semibold" style={{ color: 'var(--rhea-text-primary)' }}>Rhea</span>
                  <span className="text-xl" style={{ color: 'var(--rhea-text-accent)' }}>| {domain}</span>
                </div>
                <div className="flex-1 max-w-3xl">
                  <SearchBar 
                    onSearch={handleSearch} 
                    isLoading={isLoading} 
                    initialValue={initialQuery}
                    placeholderMessage={`Search in ${domain ? domain : '...'}`}
                    autoFocus={true}
                  />
                </div>
                <div className="ml-4">
                  <SettingsButton />
                  {!loggedIn ? <>              
                    <LoginItem title="Login to Rhea"/>
                    <SignUpItem title="Sign up for Rhea"/>
                  </> : <>
                    <LogoutItem />
                  </>}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 container mx-auto px-4 py-8">
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--rhea-text-primary)' }}>
                Results from {domain}
              </h2>
              <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory">
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="snap-center shrink-0 w-48">
                        <LoadingCard type="domain" />
                      </div>
                    ))
                  : domainResults.map((result, index) => (
                      <div key={index} className="snap-center shrink-0 w-48">
                        <DomainResultCard
                          title={result.title}
                          description={result.description}
                          url={result.url}
                          image_url={result.image_url || ''}
                          type={result.type}
                        />
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        // Show centered search when no content
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-center mb-8 w-full px-4">
            <div className="flex items-center justify-center gap-2 mb-8">
              <img src="/assets/rhea/logo.png" alt="Rhea" className="h-12" />
              <span className="text-3xl font-semibold" style={{ color: 'var(--rhea-text-primary)' }}>Rhea</span>
              <span className="text-3xl" style={{ color: 'var(--rhea-text-accent)' }}>| {domain}</span>
            </div>
            <div className="w-full max-w-6xl mx-auto">
              <SearchBar 
                onSearch={handleSearch} 
                isLoading={isLoading} 
                initialValue={initialQuery}
                placeholderMessage={`Search in ${domain} domain...`}
                autoFocus={true}
              />
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <SettingsButton />
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainSearchPage; 